// ---------------------------------------------------------------------------
// Who is in the room, and the host's powers over them, at any point in a game.
//
// **WHY THIS IS INSIDE THE CHAT PANEL AND NOT ON A PLAY SCREEN.** Kick, ban,
// mute and transfer already worked mid-game on the server — `removePlayer` has
// no phase check and the socket handlers only ask whether the sender is the
// host — and the whole of what was missing was somewhere to press them from.
// STATUS.md item 10 called that out: a disruptive player could not be removed
// mid-game, which is exactly the case the design hands to the host.
//
// A play screen is the wrong place to put the answer. It is a thirty-second
// timed surface, the host is also playing on it, and a thumb on a phone arrives
// at the edge of the board at speed. A control that kicks somebody out of a game
// and is one stray tap from the answer box is worse than one that takes an extra
// press to reach. The room shell owns everything that outlives a phase, and the
// chat panel is the piece of it that is already about people rather than about
// the round: it is mounted from the join to the last screen, it is off the play
// area on a desktop and behind a button and a scrim on a phone, and it is where
// a host who has noticed somebody being a problem is already looking, because
// being a problem mostly means saying something.
//
// So the path to a kick mid-round is: open the chat (a press on a phone, nothing
// on a desktop), switch to People (a press), open the row (a press), then Kick.
// Ban keeps its own confirmation on top of that. Nothing here is reachable from
// the board at all.
//
// **BOTS ARE NOT IN THIS LIST.** They cannot chat, they cannot be disruptive,
// and every control that is actually about a bot — difficulty, model, kind, and
// the team moves — is refused by the engine outside the lobby. A row whose menu
// held nothing but a Kick that meant "remove a seat and stop paying for it" is a
// spending decision dressed as moderation, and it belongs on the lobby screen
// that prices seats. The lobby's list is unchanged and still has all of it.
//
// The import of `HostRowMenu` reaches from `components/` into `screens/`, which
// is backwards, and it is the right trade anyway: the alternative is a second
// menu, and everything difficult about that component is in its dismissal and
// placement rules rather than in its items. See the note on `scope` there.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { ReactElement } from 'react';
import type { ClientGameState, ClientMessage, PublicPlayer } from '@countdown/shared';
import { PlayerChip } from '../primitives/PlayerChip.js';
import { HostRowMenu } from '../../screens/room/lobby/HostRowMenu.js';
import { SectionLabel } from '../../screens/room/lobby/fields.js';
import { displayColorOf, seatedPlayers, spectators } from '../../lib/playerLookup.js';

export interface RoomRosterProps {
  state: ClientGameState;
  send: (message: ClientMessage) => void;
}

export function RoomRoster({ state, send }: RoomRosterProps): ReactElement {
  // One id, so opening a menu closes the last one without either of them
  // knowing about the other. The same structure the lobby's seat list uses, and
  // for the same reason.
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const playing = seatedPlayers(state).filter((player) => !player.isBot);
  const watching = spectators(state).filter((player) => !player.isBot);

  const row = (player: PublicPlayer): ReactElement => {
    const isYou = player.id === state.youId;
    return (
      <li
        key={player.id}
        className="flex min-h-11 items-center gap-2 rounded-tile px-1.5 py-1"
      >
        <div className="min-w-0 flex-1">
          <PlayerChip
            player={player}
            color={displayColorOf(state, player)}
            showHostCrown={player.isHost}
            showMuted={player.muted}
            size="sm"
          />
        </div>
        {/* The host gets a menu on everybody but themselves. Everybody else
            gets the same empty square, so the names line up in one column
            whoever is reading — the lobby's rows do the same thing for the
            same reason. */}
        {state.youAreHost && !isYou ? (
          <HostRowMenu
            send={send}
            player={player}
            teams={state.teams}
            scope="moderation"
            open={openMenu === player.id}
            onOpenChange={(next) => setOpenMenu(next ? player.id : null)}
          />
        ) : (
          <span aria-hidden="true" className="h-11 w-11 shrink-0" />
        )}
      </li>
    );
  };

  return (
    // The same flex basis and zero minimum the message list has, so switching
    // between the two cannot change the panel's height. Nothing in the chat
    // panel may resize with its contents, and a roster is contents.
    <div
      data-testid="room-roster"
      className="flex min-h-0 shrink grow basis-[260px] flex-col gap-1 overflow-y-auto px-2 py-2 [scrollbar-gutter:stable]"
    >
      <div className="px-1.5 pt-1 pb-0.5">
        <SectionLabel>playing</SectionLabel>
      </div>
      <ul className="flex flex-col">{playing.map(row)}</ul>
      {watching.length === 0 ? null : (
        <>
          <div className="px-1.5 pt-2 pb-0.5">
            <SectionLabel>watching</SectionLabel>
          </div>
          {/* Spectators are here because Isaac asked for them by name, and
              because they are the half of the room the lobby's seat list does
              not put a menu on at all. `kick-player` takes a player id and a
              spectator has one, so nothing on the server had to change. */}
          <ul className="flex flex-col">{watching.map(row)}</ul>
        </>
      )}
    </div>
  );
}
